/**
 * /crm/settings/templates — admin-editable email templates per event-kind.
 *
 * The right-rail variable picker is sourced from the backend's whitelist so
 * the same set of merge variables is recognised at render time. Inserted
 * variables are wrapped as `{{ key }}` in the body cursor or appended.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Save, Variable } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  useNotificationTemplates,
  usePatchNotificationTemplates,
  useTemplateVariables,
  type CrmNotificationTemplate,
} from '@/api/bi/crmSettings';
import { apiErrorMessage } from '@/lib/apiError';
import { notify } from '@/lib/toast';
import { cn } from '@/lib/cn';

export function CrmTemplatesPage() {
  const templatesQuery = useNotificationTemplates();
  const variablesQuery = useTemplateVariables();
  const patch = usePatchNotificationTemplates();

  const templates = templatesQuery.data?.results ?? [];
  const [activeKind, setActiveKind] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, CrmNotificationTemplate>>({});
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  // Initialise drafts when templates first load. We don't reset every render —
  // only seed missing entries so unsaved edits survive a refetch.
  useEffect(() => {
    if (!templatesQuery.data) return;
    setDrafts((current) => {
      const next: Record<string, CrmNotificationTemplate> = { ...current };
      for (const t of templatesQuery.data.results) {
        if (!next[t.event_kind]) next[t.event_kind] = { ...t };
      }
      return next;
    });
    if (!activeKind && templatesQuery.data.results.length > 0) {
      setActiveKind(templatesQuery.data.results[0].event_kind);
    }
  }, [templatesQuery.data, activeKind]);

  const active = activeKind ? drafts[activeKind] : null;

  const dirtyKinds = useMemo(() => {
    const dirty = new Set<string>();
    for (const t of templates) {
      const draft = drafts[t.event_kind];
      if (!draft) continue;
      if (
        draft.subject !== t.subject ||
        draft.body_markdown !== t.body_markdown ||
        draft.is_active !== t.is_active
      ) {
        dirty.add(t.event_kind);
      }
    }
    return dirty;
  }, [drafts, templates]);

  function setActiveField<K extends keyof CrmNotificationTemplate>(
    key: K,
    value: CrmNotificationTemplate[K],
  ) {
    if (!activeKind) return;
    setDrafts((d) => ({
      ...d,
      [activeKind]: { ...d[activeKind], [key]: value },
    }));
  }

  function insertVariable(varKey: string) {
    if (!activeKind) return;
    const token = `{{ ${varKey} }}`;
    const ta = bodyRef.current;
    setDrafts((d) => {
      const draft = d[activeKind];
      if (!draft) return d;
      let next = draft.body_markdown ?? '';
      if (ta && document.activeElement === ta) {
        const { selectionStart, selectionEnd } = ta;
        next = next.slice(0, selectionStart) + token + next.slice(selectionEnd);
        // Restore caret after React re-renders.
        requestAnimationFrame(() => {
          ta.focus();
          ta.setSelectionRange(selectionStart + token.length, selectionStart + token.length);
        });
      } else {
        next = next ? `${next} ${token}` : token;
      }
      return { ...d, [activeKind]: { ...draft, body_markdown: next } };
    });
  }

  async function saveAll() {
    if (dirtyKinds.size === 0) {
      notify.message('Nothing to save.');
      return;
    }
    const updates = Array.from(dirtyKinds).map((kind) => {
      const t = drafts[kind];
      return {
        event_kind: kind,
        subject: t.subject,
        body_markdown: t.body_markdown,
        is_active: t.is_active,
      };
    });
    try {
      await patch.mutateAsync(updates);
      notify.success(`Saved ${updates.length} template${updates.length === 1 ? '' : 's'}.`);
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Could not save templates.'));
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="CRM Settings"
        title="Notification Templates"
        description="Edit subject + body for every CRM event. Body uses Markdown; insert variables from the right rail."
        actions={
          <Button size="sm" onClick={saveAll} disabled={patch.isPending || dirtyKinds.size === 0}>
            {patch.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
            Save{dirtyKinds.size > 0 ? ` (${dirtyKinds.size})` : ''}
          </Button>
        }
      />

      {templatesQuery.isLoading ? (
        <Skeleton className="h-96" />
      ) : templatesQuery.isError ? (
        <ErrorState
          title="Could not load templates"
          description={String((templatesQuery.error as Error)?.message ?? '')}
        />
      ) : templates.length === 0 ? (
        <Card>
          <CardBody className="py-10 text-center text-sm text-muted-foreground">
            No templates seeded. Run migrations to seed defaults.
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[220px_1fr_220px]">
          {/* Left rail — event kinds */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Events</CardTitle>
            </CardHeader>
            <CardBody className="space-y-1 p-2">
              {templates.map((t) => {
                const isDirty = dirtyKinds.has(t.event_kind);
                return (
                  <button
                    key={t.event_kind}
                    type="button"
                    onClick={() => setActiveKind(t.event_kind)}
                    className={cn(
                      'press flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
                      activeKind === t.event_kind
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-muted',
                    )}
                  >
                    <span className="truncate">{t.event_label}</span>
                    {isDirty ? <Badge tone="warning">unsaved</Badge> : null}
                  </button>
                );
              })}
            </CardBody>
          </Card>

          {/* Editor */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">
                {active ? active.event_label : 'Pick an event'}
              </CardTitle>
              {active ? (
                <Badge tone={active.is_active ? 'success' : 'neutral'}>
                  {active.is_active ? 'active' : 'inactive'}
                </Badge>
              ) : null}
            </CardHeader>
            <CardBody className="space-y-3">
              {active ? (
                <>
                  <Field label="Subject">
                    <Input
                      value={active.subject}
                      onChange={(e) => setActiveField('subject', e.target.value)}
                    />
                  </Field>
                  <Field label="Body (Markdown)">
                    <textarea
                      ref={bodyRef}
                      value={active.body_markdown}
                      onChange={(e) => setActiveField('body_markdown', e.target.value)}
                      rows={16}
                      className="w-full rounded-md border border-input bg-surface px-3 py-2 text-sm font-mono leading-relaxed focus-ring"
                    />
                  </Field>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={active.is_active}
                      onChange={(e) => setActiveField('is_active', e.target.checked)}
                    />
                    Active (uncheck to silently skip emails for this event)
                  </label>
                </>
              ) : (
                <p className="py-8 text-center text-xs text-muted-foreground">
                  Pick an event from the left rail to edit its template.
                </p>
              )}
            </CardBody>
          </Card>

          {/* Right rail — variables */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">
                <Variable className="mr-1 inline h-3.5 w-3.5" /> Variables
              </CardTitle>
            </CardHeader>
            <CardBody className="space-y-1 p-2">
              <p className="px-2 pb-2 text-[11px] text-muted-foreground">
                Click to insert at the cursor.
              </p>
              {variablesQuery.isLoading ? (
                <Skeleton className="h-24" />
              ) : (
                (variablesQuery.data?.results ?? []).map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => insertVariable(v.key)}
                    className="press flex w-full flex-col gap-0 rounded-md px-2 py-1.5 text-left text-[11px] hover:bg-muted"
                  >
                    <code className="font-mono text-foreground">{`{{ ${v.key} }}`}</code>
                    <span className="text-[10px] text-muted-foreground">{v.label}</span>
                  </button>
                ))
              )}
            </CardBody>
          </Card>
        </div>
      )}
    </>
  );
}
